import dayjs from 'dayjs';
import { ICloneable } from '../../contracts/cloneable.js';
import { IDataComparable } from '../../contracts/data-comparable.js';
import { Country } from './country.js';
import { LocalizedString } from './localized-string.js';
import { SerializationContext, SerializedData } from '../dto/data-model.js';
import { MapUtils } from '../../utility/map-utils.js';
import { fromEnumAsString, toEnumFromString, toEnumFromStringOrNull } from '../../serialization/enum-utils.js';
import {
  cloneData, cloneDataOrNull, cloneList, isSameData, isSameList,
} from '../dto/data-model-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { Price } from './price.js';
import { isNullOrUndefined } from '../../utility/null-safety.js';
import { EventCategory } from './event-category.js';
import { EventDateFormat } from '../../components/events/date-format-component.js';
import { Language } from './language.js';

export class EventInfo implements ICloneable, IDataComparable {
  private constructor(
    public readonly uniqueId: string,
    public readonly webRoute: string,
    public readonly country: Country,
    public readonly localizedName: LocalizedString,
    public readonly startDate: dayjs.Dayjs,
    public readonly creationDate: dayjs.Dayjs,
    public readonly productionId: string | null,
    public readonly coverImage: string,
    public readonly pricingInfo: PricingInfo | null,
    public readonly locationInfo: LocationInfo,
    public readonly classificationInfo: ClassificationInfo,
    public readonly eventDateFormat: EventDateFormat,
    public readonly passEventOverrideLink: string | null,
    public readonly thirdPartyEventUrl: string | null,
    public readonly freeEventUrl: string | null,
  ) { }

  static create(args: {
    uniqueId: string,
    webRoute: string,
    country: Country,
    localizedName: LocalizedString,
    startDate: dayjs.Dayjs,
    creationDate: dayjs.Dayjs,
    productionId: string | null,
    coverImage: string,
    pricingInfo: PricingInfo | null,
    locationInfo: LocationInfo,
    classificationInfo: ClassificationInfo,
    eventDateFormat: EventDateFormat,
    passEventOverrideLink: string | null,
    thirdPartyEventUrl: string | null,
    freeEventUrl: string | null,
  }): EventInfo {
    return new EventInfo(
        args.uniqueId,
        args.webRoute,
        args.country,
        args.localizedName,
        args.startDate,
        args.creationDate,
        args.productionId,
        args.coverImage,
        args.pricingInfo,
        args.locationInfo,
        args.classificationInfo,
        args.eventDateFormat,
        args.passEventOverrideLink,
        args.thirdPartyEventUrl,
        args.freeEventUrl,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): EventInfo {
    return new EventInfo(
        MapUtils.getString(map, 'uniqueId'),
        MapUtils.getString(map, 'webRoute'),
        toEnumFromString(MapUtils.getString(map, 'country'), Country),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'localizedName')),
        context.decodeDate(MapUtils.getNumber(map, 'startDate')),
        context.decodeDate(MapUtils.getNumber(map, 'creationDate')),
        MapUtils.getStringOrNull(map, 'productionId'),
        MapUtils.getString(map, 'coverImage'),
        PricingInfo.fromMapOrNull(context, MapUtils.getOrNull<SerializedData>(map, 'pricingInfo')),
        LocationInfo.fromMap(context, MapUtils.get<SerializedData>(map, 'locationInfo')),
        ClassificationInfo.fromMap(context, MapUtils.get<SerializedData>(map, 'classificationInfo')),
        toEnumFromString(MapUtils.getString(map, 'eventDateFormat'), EventDateFormat),
        MapUtils.getStringOrNull(map, 'passEventOverrideLink'),
        MapUtils.getStringOrNull(map, 'thirdPartyEventUrl'),
        MapUtils.getStringOrNull(map, 'freeEventUrl'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      uniqueId: this.uniqueId,
      webRoute: this.webRoute,
      country: fromEnumAsString(this.country),
      localizedName: context.toData(this.localizedName),
      startDate: context.encodeDate(this.startDate),
      creationDate: context.encodeDate(this.creationDate),
      productionId: this.productionId,
      coverImage: this.coverImage,
      pricingInfo: this.pricingInfo?.toMap(context) ?? null,
      locationInfo: this.locationInfo.toMap(context),
      classificationInfo: this.classificationInfo.toMap(context),
      eventDateFormat: fromEnumAsString(this.eventDateFormat),
      passEventOverrideLink: this.passEventOverrideLink,
      thirdPartyEventUrl: this.thirdPartyEventUrl,
      freeEventUrl: this.freeEventUrl,
    };
  }

  clone(): this {
    return new EventInfo(
        this.uniqueId,
        this.webRoute,
        this.country,
        cloneData(this.localizedName),
        TimeZone.cloneDate(this.startDate),
        TimeZone.cloneDate(this.creationDate),
        this.productionId,
        this.coverImage,
        cloneDataOrNull(this.pricingInfo),
        cloneData(this.locationInfo),
        cloneData(this.classificationInfo),
        this.eventDateFormat,
        this.passEventOverrideLink,
        this.thirdPartyEventUrl,
        this.freeEventUrl,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.webRoute === this.webRoute &&
      other.country === this.country &&
      isSameData(other.localizedName, this.localizedName) &&
      other.startDate === this.startDate &&
      other.creationDate === this.creationDate &&
      other.productionId === this.productionId &&
      other.coverImage === this.coverImage &&
      isSameData(other.pricingInfo, this.pricingInfo) &&
      isSameData(other.locationInfo, this.locationInfo) &&
      isSameData(other.classificationInfo, this.classificationInfo) &&
      other.eventDateFormat === this.eventDateFormat &&
      other.passEventOverrideLink === this.passEventOverrideLink &&
      other.thirdPartyEventUrl === this.thirdPartyEventUrl &&
      other.freeEventUrl === this.freeEventUrl;
  }
}

export enum SaleState {
  Open = 'Open',
  Upcoming = 'Upcoming',
  SoldOut = 'SoldOut',
}

export class PricingInfo implements ICloneable, IDataComparable {
  private constructor(
    public readonly saleState: SaleState,
    public readonly remainingTicketCount: number | null,
    public readonly minPrice: Price | null,
    public readonly maxPrice: Price | null,
  ) {}

  static create(args: {
    saleState: SaleState,
    remainingTicketCount: number | null,
    minPrice: Price | null,
    maxPrice: Price | null,
  }): PricingInfo {
    return new PricingInfo(
        args.saleState,
        args.remainingTicketCount,
        args.minPrice,
        args.maxPrice,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): PricingInfo {
    const saleState = toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'saleState'), SaleState) ??
      ((MapUtils.getBooleanOrNull(map, 'isSoldOut') ?? false) ? SaleState.SoldOut : SaleState.Open);

    return this.create({
      saleState: saleState,
      remainingTicketCount: MapUtils.getIntegerOrNull(map, 'remainingTicketCount'),
      minPrice: Price.decodeOrNull(MapUtils.getStringOrNull(map, 'minPrice')),
      maxPrice: Price.decodeOrNull(MapUtils.getStringOrNull(map, 'maxPrice')),
    });
  }

  static fromMapOrNull(context: SerializationContext, map: SerializedData | null | undefined): PricingInfo | null {
    if(isNullOrUndefined(map)) {
      return null;
    }

    return PricingInfo.fromMap(context, map);
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      saleState: fromEnumAsString(this.saleState),

      // For backward compatibility
      isSoldOut: this.saleState === SaleState.SoldOut,

      remainingTicketCount: this.remainingTicketCount,
      minPrice: this.minPrice?.encode() ?? null,
      maxPrice: this.maxPrice?.encode() ?? null,
    };
  }

  clone(): this {
    return PricingInfo.create({
      saleState: this.saleState,
      remainingTicketCount: this.remainingTicketCount,
      minPrice: cloneDataOrNull(this.minPrice),
      maxPrice: cloneDataOrNull(this.maxPrice),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.saleState === this.saleState &&
      other.remainingTicketCount === this.remainingTicketCount &&
      isSameData(other.minPrice, this.minPrice) &&
      isSameData(other.maxPrice, this.maxPrice);
  }
}

export class LocationInfo implements ICloneable, IDataComparable {
  private constructor(
    public readonly localizedName: LocalizedString,
    public readonly localizedCity: LocalizedString | null,
    public readonly localizedAddress: LocalizedString | null,
  ) { }

  static create(args: {
    localizedName: LocalizedString,
    localizedCity: LocalizedString | null,
    localizedAddress: LocalizedString | null,
  }): LocationInfo {
    return new LocationInfo(
        args.localizedName,
        args.localizedCity ?? null,
        args.localizedAddress ?? null,
    );
  }

  // For backward compatibility
  get city(): string | null {
    return this.localizedCity?.getAny() ?? null;
  }

  // For backward compatibility
  get address(): string | null {
    return this.localizedAddress?.getAny() ?? null;
  }

  static fromMap(context: SerializationContext, map: SerializedData): LocationInfo {
    const fromLocalizedOrLegacy = (localizedKey: string, legacyKey: string): LocalizedString | null => {
      const localized = MapUtils.getOrNull<SerializedData>(map, localizedKey);
      if(localized != null) {
        return context.fromData<LocalizedString>(localized);
      }

      const legacy = MapUtils.getStringOrNull(map, legacyKey);
      return legacy == null ? null : LocalizedString.forLanguage(
          context.storage.get<Language>('language', Language.English) ?? Language.English,
          legacy,
      );
    };

    return new LocationInfo(
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'localizedName')),
        fromLocalizedOrLegacy('localizedCity', 'city'),
        fromLocalizedOrLegacy('localizedAddress', 'address'),
    );
  }

  static fromMapOrNull(
      context: SerializationContext,
      map: SerializedData | null | undefined,
  ): LocationInfo | null {
    if(isNullOrUndefined(map)) {
      return null;
    }

    return LocationInfo.fromMap(context, map);
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      localizedName: context.toData(this.localizedName),
      localizedCity: this.localizedCity == null ? null : context.toData(this.localizedCity),
      localizedAddress: this.localizedAddress == null ? null : context.toData(this.localizedAddress),
      city: this.city,
      address: this.address,
    };
  }

  clone(): this {
    return new LocationInfo(
        cloneData(this.localizedName),
        cloneDataOrNull(this.localizedCity),
        cloneDataOrNull(this.localizedAddress),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.localizedName, this.localizedName) &&
      isSameData(other.localizedCity, this.localizedCity) &&
      isSameData(other.localizedAddress, this.localizedAddress);
  }
}

export class ClassificationInfo implements ICloneable, IDataComparable {
  private constructor(
    public readonly classification: EventCategory[],
    public readonly isFeatured: boolean,
    public readonly featuredScore: number,
  ) { }

  static create(args: {
    classification: EventCategory[],
    isFeatured: boolean,
    featuredScore: number,
  }): ClassificationInfo {
    return new ClassificationInfo(
        args.classification,
        args.isFeatured,
        args.featuredScore,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): ClassificationInfo {
    return new ClassificationInfo(
        MapUtils.getList<EventCategory>(map, 'classification', (x) => toEnumFromString(x as string, EventCategory)),
        MapUtils.getBoolean(map, 'isFeatured'),
        MapUtils.getNumber(map, 'featuredScore'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      classification: this.classification.map((x) => fromEnumAsString(x)),
      isFeatured: this.isFeatured,
      featuredScore: this.featuredScore,
    };
  }

  clone(): this {
    return new ClassificationInfo(
        cloneList(this.classification),
        this.isFeatured,
        this.featuredScore,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameList(other.classification, this.classification, (a, b) => a === b) &&
      other.isFeatured === this.isFeatured &&
      other.featuredScore === this.featuredScore;
  }
}
